'use client';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import React, { useState } from 'react';

import {
  GlobalPreloader,
  Loading,
  SynchronizationInfo,
  BgContainer,
  SynchronizationInfoContainer,
} from 'components';
import { colors, styled } from 'core/styles';
import { useServerData } from 'utils/react';
import { getProtocolStatsPageData } from 'api/selectors/pages';
import { ProductSlug, VersionSlug, NetworkSlug } from 'domain/types';

import { Assets, Markets } from './components';
import { TotalBalanceWidget } from '../widgets';

const tabs = {
  assets: { label: 'Assets', value: 'assets' },
  markets: { label: 'Markets', value: 'markets' },
} as const;

type TabValue = (typeof tabs)[keyof typeof tabs]['value'];

export type ProtocolStatsProps = {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
  pageData: string;
  marketsTableData: string;
};

export function ProtocolStats({
  productSlug,
  versionSlug,
  network,
  pageData,
  marketsTableData,
}: ProtocolStatsProps) {
  const [activeTab, setActiveTab] = useState<TabValue>('markets');

  const pageDataRD = useServerData(
    () => getProtocolStatsPageData({ network, productSlug, versionSlug }),
    [network, productSlug, versionSlug],
    pageData,
  );

  const handleChangeTab = (_event: React.SyntheticEvent<Element, Event>, value: TabValue) => {
    setActiveTab(value);
  };

  return (
    <Loading data={pageDataRD} loader={<GlobalPreloader />}>
      {({ balance, balanceDayAgo, hasOraclePrice, hasUnknownPrice, updatedAt }) => (
        <>
          <TotalBalanceWidget balance={balance} balanceDayAgo={balanceDayAgo} />
          <SynchronizationInfoContainer>
            <SynchronizationInfo
              timestamp={updatedAt}
              hasOraclePrice={hasOraclePrice}
              hasUnknownPrice={hasUnknownPrice}
            />
          </SynchronizationInfoContainer>

          <BgContainer zeroPadding>
            <TabsContainer>
              <Tabs value={activeTab} onChange={handleChangeTab} variant="standard">
                <Tab label={tabs.markets.label} value={tabs.markets.value} />
                <Tab label={tabs.assets.label} value={tabs.assets.value} />
              </Tabs>
            </TabsContainer>

            {activeTab === 'markets' && (
              <Markets
                network={network}
                productSlug={productSlug}
                versionSlug={versionSlug}
                data={marketsTableData}
              />
            )}
            {activeTab === 'assets' && (
              <Assets network={network} productSlug={productSlug} versionSlug={versionSlug} />
            )}
          </BgContainer>
        </>
      )}
    </Loading>
  );
}

const TabsContainer = styled('div')(() => ({
  position: 'relative',
  fontSize: 14,

  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    width: '100%',
    borderBottom: `1px solid ${colors.whiteOpacity[12]}`,
  },
}));
